import React from 'react';
import { Box, Typography } from '@mui/material';
import {
  dimGrey,
  inactive,
  lightBlack,
  lightTeal,
} from '../../../../../mainTheme';
import type { FacilitySurveyParticipation } from '../../../../../store/facility/facilityTypes';
import { formatRate } from './facilitySurveyInsights';

interface SurveyParticipationProps {
  participation: FacilitySurveyParticipation;
}

const Legend = ({
  color,
  label,
  value,
}: {
  color: string;
  label: string;
  value: number;
}) => (
  <Box display="flex" alignItems="center" gap={1} minWidth={0}>
    <Box
      sx={{
        width: 10,
        height: 10,
        borderRadius: '2px',
        backgroundColor: color,
        flexShrink: 0,
      }}
    />
    <Typography
      sx={{
        color: dimGrey,
        fontSize: '12px',
        fontWeight: 500,
        lineHeight: '16px',
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        color: lightBlack,
        fontSize: '12px',
        fontWeight: 700,
        lineHeight: '16px',
      }}
    >
      {value}
    </Typography>
  </Box>
);

// Who answered vs. who did not, as one proportional bar over the patients
// eligible in the window (assigned to the location at any point in it): the
// share that answered is the participation rate the API computed.
const SurveyParticipation: React.FC<SurveyParticipationProps> = ({
  participation,
}) => {
  const eligible = participation.patients_eligible;
  const responded = Math.min(participation.patients_responded, eligible);
  const share = eligible ? (responded / eligible) * 100 : 0;
  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={1.5}
      data-testid="survey-participation"
    >
      <Box display="flex" alignItems="baseline" gap={1}>
        <Typography
          sx={{
            color: lightBlack,
            fontSize: '28px',
            fontWeight: 700,
            lineHeight: '36px',
          }}
        >
          {formatRate(participation.participation_rate)}
        </Typography>
        <Typography
          sx={{
            color: dimGrey,
            fontSize: '12px',
            fontWeight: 500,
            lineHeight: '16px',
          }}
        >
          of {eligible} patient{eligible === 1 ? '' : 's'} answered
        </Typography>
      </Box>
      <Box
        role="progressbar"
        aria-label="Participation"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(share)}
        sx={{
          height: 12,
          borderRadius: '6px',
          backgroundColor: inactive,
          overflow: 'hidden',
        }}
      >
        <Box
          data-testid="survey-participation-fill"
          sx={{
            width: `${share}%`,
            height: '100%',
            backgroundColor: lightTeal,
            borderRadius: '6px',
          }}
        />
      </Box>
      <Box display="flex" flexWrap="wrap" gap={3}>
        <Legend color={lightTeal} label="Answered" value={responded} />
        <Legend
          color={inactive}
          label="Did not answer"
          value={participation.patients_not_responded}
        />
      </Box>
    </Box>
  );
};

export default SurveyParticipation;
